import React, { useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { useAuth } from "../../modules/auth";
import { formatDateTime } from "../../utils/Utils";

export function LoaderSlip({ show, onClose, slipData, mode = "SALE" }) {
    const { currentUser } = useAuth();
    const printRef = useRef(null);

    const orgName = currentUser?.orgName || currentUser?.companyName || "Organization";
    const isPurchase = mode === "PURCHASE";

    // Labels vary by mode so the same slip works for both sales (empty truck arrival)
    // and purchases (loaded truck arrival from supplier).
    const slipTitleText = isPurchase ? "--- UNLOADER SLIP ---" : "--- LOADER SLIP ---";
    const partyLabel = isPurchase ? "Supplier:" : "Customer:";
    const weightLabel = isPurchase ? "LOADED WEIGHT" : "EMPTY WEIGHT";
    const instructionLine1 = isPurchase
        ? "Present this slip at the unloading area."
        : "Present this slip at the loading area.";
    const instructionLine2 = isPurchase
        ? "Proceed to weighbridge after unloading."
        : "Proceed to weighbridge after loading.";

    const handlePrint = () => {
        const printContent = printRef.current;
        if (!printContent) return;

        const printWindow = window.open("", "_blank", "width=400,height=600");
        printWindow.document.write(`
            <html>
            <head>
                <title>Loader Slip</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Courier New', monospace; font-size: 12px; padding: 8px; }
                    @media print {
                        @page { size: 80mm auto; margin: 2mm; }
                        body { width: 76mm; }
                    }
                </style>
            </head>
            <body>${printContent.innerHTML}</body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);
    };

    if (!slipData) return null;

    return (
        <Modal show={show} onHide={onClose} centered size="sm">
            <Modal.Header closeButton className="py-2 d-print-none">
                <Modal.Title style={{ fontSize: "14px" }}>
                    <i className="bi bi-receipt me-2"></i>
                    {isPurchase ? "Unloader Slip" : "Loader Slip"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-2">
                <div ref={printRef}>
                    <div style={styles.slip}>
                        {/* Header */}
                        <div style={styles.header}>
                            <div style={styles.orgName}>{orgName}</div>
                            <div style={styles.slipTitle}>{slipTitleText}</div>
                        </div>

                        <div style={styles.divider}>{'─'.repeat(40)}</div>

                        {/* Date & Time */}
                        <div style={styles.row}>
                            <span style={styles.label}>Date:</span>
                            <span style={styles.value}>{formatDateTime(slipData.date)}</span>
                        </div>

                        <div style={styles.divider}>{'─'.repeat(40)}</div>

                        {/* Vehicle */}
                        <div style={styles.row}>
                            <span style={styles.label}>Vehicle:</span>
                            <span style={styles.valueBold}>{slipData.vehicleNumber || "-"}</span>
                        </div>

                        {/* Customer / Supplier */}
                        <div style={styles.row}>
                            <span style={styles.label}>{partyLabel}</span>
                            <span style={styles.value}>{slipData.customerName || "-"}</span>
                        </div>

                        <div style={styles.divider}>{'─'.repeat(40)}</div>

                        {/* Product */}
                        <div style={styles.productSection}>
                            <div style={styles.productLabel}>PRODUCT</div>
                            <div style={styles.productName}>{slipData.productName || "-"}</div>
                        </div>

                        <div style={styles.divider}>{'─'.repeat(40)}</div>

                        {/* Weight (empty for sales, loaded for purchases).
                            When a purchase correction exists, show only the effective loaded weight
                            — the raw loaded weight and the correction line are internal accounting,
                            not info the unloader operator needs on the slip. */}
                        {(() => {
                            const correction = isPurchase ? parseFloat(slipData.correction) || 0 : 0;
                            const hasCorrection = correction > 0;
                            if (hasCorrection) {
                                return (
                                    <div style={styles.weightSection}>
                                        <div style={styles.weightLabel}>EFFECTIVE LOADED</div>
                                        <div style={styles.weightValue}>
                                            {(parseFloat(slipData.emptyWeight) - correction).toFixed(3)} Ton
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <div style={styles.weightSection}>
                                    <div style={styles.weightLabel}>{weightLabel}</div>
                                    <div style={styles.weightValue}>
                                        {slipData.emptyWeight ? `${slipData.emptyWeight} Ton` : "-"}
                                    </div>
                                </div>
                            );
                        })()}

                        <div style={styles.divider}>{'─'.repeat(40)}</div>

                        {/* Operator */}
                        <div style={styles.row}>
                            <span style={styles.label}>Operator:</span>
                            <span style={styles.value}>{slipData.operatorName || "-"}</span>
                        </div>

                        <div style={styles.divider}>{'─'.repeat(40)}</div>

                        {/* Instruction */}
                        <div style={styles.instruction}>
                            {instructionLine1}
                        </div>
                        <div style={styles.instruction}>
                            {instructionLine2}
                        </div>

                        <div style={styles.divider}>{'─'.repeat(40)}</div>

                        <div style={styles.footer}>*** Thank You ***</div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="py-2 d-print-none">
                <Button variant="outline-secondary" size="sm" onClick={onClose}>
                    Close
                </Button>
                <Button variant="primary" size="sm" onClick={handlePrint}>
                    <i className="bi bi-printer me-2"></i>
                    Print Slip
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const styles = {
    slip: {
        fontFamily: "'Courier New', monospace",
        fontSize: "12px",
        lineHeight: "1.5",
        padding: "8px",
        color: "#000",
        backgroundColor: "#fff",
    },
    header: {
        textAlign: "center",
        marginBottom: "4px",
    },
    orgName: {
        fontSize: "16px",
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: "1px",
    },
    slipTitle: {
        fontSize: "13px",
        fontWeight: "bold",
        marginTop: "2px",
    },
    divider: {
        textAlign: "center",
        color: "#999",
        fontSize: "10px",
        lineHeight: "1.2",
        margin: "4px 0",
        overflow: "hidden",
        whiteSpace: "nowrap",
    },
    row: {
        display: "flex",
        justifyContent: "space-between",
        padding: "2px 0",
    },
    label: {
        color: "#555",
        fontSize: "11px",
    },
    value: {
        fontWeight: "500",
        fontSize: "12px",
        textAlign: "right",
    },
    valueBold: {
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "right",
    },
    productSection: {
        textAlign: "center",
        padding: "6px 0",
    },
    productLabel: {
        fontSize: "10px",
        color: "#666",
        letterSpacing: "1px",
    },
    productName: {
        fontSize: "15px",
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    weightSection: {
        textAlign: "center",
        padding: "6px 0",
    },
    weightLabel: {
        fontSize: "10px",
        color: "#666",
        letterSpacing: "1px",
    },
    weightValue: {
        fontSize: "18px",
        fontWeight: "bold",
    },
    weightValueSmall: {
        fontSize: "13px",
        fontWeight: "normal",
        color: "#666",
    },
    instruction: {
        textAlign: "center",
        fontSize: "10px",
        fontStyle: "italic",
        color: "#555",
        padding: "1px 0",
    },
    footer: {
        textAlign: "center",
        fontSize: "11px",
        fontWeight: "bold",
        marginTop: "2px",
    },
};
